import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPlus, faPenToSquare, faEyeSlash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

import MyProfilesidebar from './MyProfilesidebar';
import Billingform from '../BillingAddresspage/Billingform';
import Popup from '../RegisterScreen/Popup';
import { api } from '../../Constant';

const MyProfileOverview = () => {
    const navigate = useNavigate();

    // --- Refactored State Management ---
    const [view, setView] = useState('OVERVIEW'); // 'OVERVIEW', 'ADD_FORM', 'RESET_PASSWORD'
    const [addressList, setAddressList] = useState([]);
    const [profile, setProfile] = useState({ name: '', email: '', phone: '' });

    // State for password reset form
    const [passwordData, setPasswordData] = useState({ old: '', new: '', confirm: '' });
    const [showPassword, setShowPassword] = useState({ old: false, new: false, confirm: false });
    
    // State for loading and popups
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [popup, setPopup] = useState({ visible: false, message: '', type: 'success' });

    // --- Reliable Guest User Check ---
    const isGuest = useCallback(() => {
        const userId = localStorage.getItem('userId');
        const guestId = localStorage.getItem('guest_id');
        return guestId && userId === guestId;
    }, []);

    // --- API Identifier Logic (Handles Guest/User) ---
    const getApiIdentifier = useCallback(() => {
        const userId = localStorage.getItem('userId');
        if (isGuest()) {
            return { guest_id: localStorage.getItem('guest_id') };
        }
        if (userId) {
            return { user_id: userId };
        }
        return null;
    }, [isGuest]);
    
    // --- Data Fetching ---
    const fetchAddressList = useCallback(async () => {
        const identifier = getApiIdentifier();
        if (!identifier) return;
        try {
            const response = await axios.post(api + "address_list", identifier);
            if (response.data.success === "true") {
                setAddressList(response.data.address || []);
            }
        } catch (error) {
            console.error("Error fetching address list:", error);
            setPopup({ visible: true, message: 'Could not fetch addresses.', type: 'error' });
        }
    }, [getApiIdentifier]);

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            setProfile({
                name: localStorage.getItem('userName') || '',
                email: localStorage.getItem('userEmail') || '',
                phone: localStorage.getItem('userPhone') || '',
            });
            await fetchAddressList();
            setIsLoading(false);
        };
        loadInitialData();
    }, [fetchAddressList]);


    // --- Action Handlers ---
    const handleSetDefaultAddress = async (addressId) => {
        const identifier = getApiIdentifier();
        if (!identifier) return;

        setIsSubmitting(true);
        try {
            const response = await axios.post(api + "address_active", { ...identifier, address_id: addressId });
            if (response.data.success === "true") {
                await fetchAddressList(); // Re-fetch list to show updated status without page reload
            }
        } catch (error) {
            console.error("Error updating default address:", error);
            setPopup({ visible: true, message: 'Could not set default address.', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveAddress = async (addressId) => {
        const identifier = getApiIdentifier();
        if (!identifier) return;

        // Optimistic UI update
        const originalList = [...addressList];
        setAddressList(prev => prev.filter(addr => addr.address_id !== addressId));
        
        try {
            const response = await axios.post(api + "delete_address", { ...identifier, address_id: addressId });
            if (response.data.success !== "true") {
                setAddressList(originalList); // Revert on failure
                setPopup({ visible: true, message: 'Failed to delete address.', type: 'error' });
            }
        } catch (error) {
            setAddressList(originalList); // Revert on error
            console.error('Error deleting address:', error);
            setPopup({ visible: true, message: 'An error occurred while deleting.', type: 'error' });
        }
    };
    
    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (passwordData.new !== passwordData.confirm) {
            setPopup({ visible: true, message: 'New passwords do not match.', type: 'error' });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post(api + 'change_password', {
                user_id: localStorage.getItem('userId'),
                old_password: passwordData.old,
                password: passwordData.new,
                password_confirmation: passwordData.confirm,
            });
            if (response.data.success === 'true') {
                setPopup({ visible: true, message: 'Password reset successfully! Please log in again.', type: 'success' });
                setTimeout(() => {
                    localStorage.clear(); // Clear all session data
                    navigate('/Login-Page');
                }, 2000);
            } else {
                setPopup({ visible: true, message: response.data.message || 'Failed to change password.', type: 'error' });
            }
        } catch (error) {
            setPopup({ visible: true, message: 'An error occurred.', type: 'error' });
            console.error('Error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // --- Helper Functions for UI ---
    const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    const toggleShowPassword = (field) => setShowPassword({ ...showPassword, [field]: !showPassword[field] });
    
    if (isLoading) {
        return (
            <div className="loading-container pt-80 pb-80" style={{textAlign: 'center', minHeight: '300px'}}>
                <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
        );
    }

    return (
        <div className='myprofile-bg pt-40 pb-40'>
            <div className='container'>
                <div className='row'>
                    <div className='col-lg-3'>
                        <MyProfilesidebar />
                    </div>
                    <div className='col-lg-9'>
                        <div className='myprofile-overview'>
                            
                            {view === 'OVERVIEW' && (
                                <>
                                    {/* Profile Header */}
                                    <div className='row'>
                                        <div className='col-lg-12 myprofile-container'>
                                            <div className='tittle-nameradius'>
                                                <h1>{profile.name ? profile.name.charAt(0).toUpperCase() : 'G'}</h1>
                                            </div>
                                            <div className='title-headmail'>
                                                <div className='main-title'>
                                                    <h1>{profile.name || 'Guest User'}</h1>
                                                    {!isGuest() && <FontAwesomeIcon icon={faPenToSquare} style={{ cursor: 'pointer' }} onClick={() => navigate('/Profileupdate')} />}
                                                </div>
                                                {!isGuest() ? (
                                                    <>
                                                        <p>{profile.email || 'Email Not Available'}</p>
                                                        <p>{profile.phone || 'Phone Not Available'}</p>
                                                    </>
                                                ) : <p>You are logged in as a guest.</p>}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Address List */}
                                    <div className='row content-mails'>
                                        <div className='col-lg-2'>
                                            <div className='addAddress-main'>
                                                <div className='address-icon-bg' onClick={() => setView('ADD_FORM')}>
                                                    <FontAwesomeIcon icon={faPlus} />
                                                </div>
                                                <p>Add Address</p>
                                            </div>
                                        </div>
                                        {addressList.map((item) => (
                                            <div className='col-lg-5' key={item.address_id}>
                                                <div className='table-bg-address'>
                                                    <div className='table-tittle'>
                                                        <div className='tittle' onClick={() => handleSetDefaultAddress(item.address_id)} style={{cursor: 'pointer'}}>
                                                            <p>{item.active_status === "Active" ? "Default Address" : "Make Default"}</p>
                                                        </div>
                                                        <div className='edit-sec'>
                                                            <a className='right' onClick={() => navigate(`/Myprofileedit/${item.address_id}`)}>Edit</a>
                                                            <a onClick={() => handleRemoveAddress(item.address_id)}>Remove</a>
                                                        </div>
                                                    </div>
                                                    <div className='mail-address'>
                                                        <h6>{item.first_name} {item.last_name}</h6>
                                                        <p>{item.address}</p>
                                                        <p>{item.phone_number}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Reset Password Button */}
                                    {!isGuest() && (
                                        <div className='row'>
                                            <div className='col-lg-12 resetpassword'>
                                                <button onClick={() => setView('RESET_PASSWORD')}>RESET PASSWORD</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {/* --- Conditional Views --- */}
                            {view === 'ADD_FORM' && (
                                <div className='row'>
                                    <Billingform event={() => setView('OVERVIEW')} />
                                </div>
                            )}

                            {view === 'RESET_PASSWORD' && (
                                <div className='row ResetPwd-form'>
                                    <div className='col-lg-12'><h6>Reset Your Password</h6></div>
                                    <form className='row inner-main-form' onSubmit={handleResetPassword}>
                                        {['old', 'new', 'confirm'].map(field => (
                                            <div className='col-lg-7' key={field}>
                                                <label>{field.charAt(0).toUpperCase() + field.slice(1)} Password <sup>*</sup></label><br />
                                                <div className='password-field'>
                                                    <input type={showPassword[field] ? 'text' : 'password'} name={field} placeholder={`${field.charAt(0).toUpperCase() + field.slice(1)} Password`} value={passwordData[field]} onChange={handlePasswordChange} required disabled={isSubmitting} />
                                                    <button type='button' onClick={() => toggleShowPassword(field)} className='eye-icon'>
                                                        <FontAwesomeIcon icon={showPassword[field] ? faEyeSlash : faEye} color='#227070' />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <div className='col-lg-12 reset-button'>
                                            <button type='submit' className='submit' disabled={isSubmitting}>{isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}</button>
                                            <button type='button' className='cancel' onClick={() => setView('OVERVIEW')}>CANCEL</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {popup.visible && (
                                <Popup message={popup.message} title="Notice" type={popup.type} onClose={() => setPopup({ ...popup, visible: false })} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MyProfileOverview;